import React, {useState, useMemo, useEffect} from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import Icon from 'components/icons/icon';

import theme, {bp, color} from 'settings/theme';

import storyblokService from 'services/storyblokService.js';


type Props = {
  product: Product
};


// const formatPrice = (value) => {
//     // First replace gets rid of unnecessry zeros (e.g. 34.00 becomes 34). Second replace puts commas into thousands and millions
//     return (Math.round(value * 100) / 100).toFixed(2).replace(/(\d+)([.,]0+)/, "$1").replace(/\B(?=(\d{3})+(?!\d))/g, ",")
// }

const formatPrice = (value) => {
  let price: any = (Math.round(value * 100) / 100);        // Sets precision to 2 - e.g. removes lots of extra numbers
  price = price.toFixed(2);                                // Makes it 2 decimal places
  price = price.replace(/(\.|,)00$/g, '');                 // Removes double zero after decimal point - e.g. 37.00 becomes 37
  price = price.replace(/\B(?=(\d{3})+(?!\d))/g, ",");     // Adds commas into thousands and millions
  return price;
};

const addVat = (value) => {
  return formatPrice(value * 1.2)
};


// Component
const Component: React.FC<Props> = (props) => {

  // All Images
  const images: ProductImage[] = useMemo(() => {

    const imageArr = props.product.images.map(item => {
      item.image.url = item.image.url ? storyblokService.createResizedImageUrl(item.image.url, 400) : '/images/no-image.png';
      return item;
    });

    return imageArr;
      
  }, [props.product.images]);


  // Thumbs
  const thumbs = useMemo(() => {
    return images.length > 1 ? images.slice(1,4) : [];
  }, [images])


  return (

    <>

      <StyledComponent className="searchCard">

        <div className="searchCard-layout">

          {/* Images  */}
          <div className="searchCard-layout-images">

            {/* Main Image */}
            {images[0] && 
              <div className="searchCard-mainImage">
                <Link href="/products/[slug]/[[...tab]]" as={`/products/${props.product.slug}`}>
                  <img src={images[0].image.url} loading="lazy" alt={images[0].alt_text ?? 'Main Product Image'} />
                </Link>
              </div>
            }

            {/* Gallery */}
            <div className="searchCard-thumbs">
              {thumbs.map((item, index) => (
                <Link href="/products/[slug]/[[...tab]]" as={`/products/${props.product.slug}`} key={index}>
                  <img src={item.image.url} loading="lazy" alt={item.alt_text ?? 'Product Image'} />
                </Link>
              ))}
            </div>

            {/* South East */}
            {props.product.availability.region === 'se' &&
              <div className="searchCard-southEast">
                <div className="searchCard-southEast-pic"><img src="/images/se-map.png" alt="South East and London Map" /></div>
                <div className="searchCard-southEast-text">This product is only available in London and the South East</div>
              </div>
            }

          </div>


          {/* Details  */}
          <div className="searchCard-layout-details">
            <div className="searchCard-title">
              <Link href="/products/[slug]/[[...tab]]" as={`/products/${props.product.slug}`}>
                {props.product.name}
              </Link>
            </div>
            {props.product.summary &&
              <div className="searchCard-summary">
                {props.product.summary}
              </div>
            }
          </div>


          {/* Pricing  */}
          {(props.product.pricing.buy || props.product.pricing.buyUsed || props.product.pricing.motability || props.product.pricing.shortTermHire || props.product.pricing.longTermHire) &&

            <div className="searchCard-layout-pricing">
              <table className="searchCard-pricingTable">
                <thead>
                  <tr>
                    <th>&nbsp;</th>
                    <th><strong>Ex. VAT</strong></th>
                    <th><strong>Inc. VAT</strong></th>
                  </tr>
                </thead>
                <tbody>
                  {props.product.pricing.shortTermHire?.price1! > 0 && 
                    <tr>
                      <td><strong>Short Term Hire</strong></td>
                      <td>£{formatPrice(props.product.pricing.shortTermHire?.price1)} pw</td>
                      <td>£{addVat(props.product.pricing.shortTermHire?.price1)} pw</td>
                    </tr>
                  }
                  {props.product.pricing.longTermHire?.threeYearWeeklyPrice! > 0 && 
                    <tr>
                      <td><strong>Long Term Hire</strong></td>
                      <td>From £{formatPrice(props.product.pricing.longTermHire?.threeYearWeeklyPrice)} pw</td>
                      <td>From £{addVat(props.product.pricing.longTermHire?.threeYearWeeklyPrice)} pw</td>
                    </tr>
                  }
                  {props.product.pricing.motability?.weeklyPrice! > 0 && 
                    <tr>
                      <td><strong>Motability</strong></td>
                      <td>£{formatPrice(props.product.pricing.motability?.weeklyPrice)} pw</td>
                      <td>N/A</td>
                    </tr>
                  }
                  {props.product.pricing.buy?.purchasePrice! > 0 && 
                    <tr>
                      <td><strong>Purchase Price</strong></td>
                      <td>£{formatPrice(props.product.pricing.buy?.purchasePrice)}</td>
                      <td>£{addVat(props.product.pricing.buy?.purchasePrice)}</td>
                    </tr>
                  }
                  {props.product.pricing.buyUsed?.purchasePrice! > 0 && 
                    <tr>
                      <td><strong>Purchase Price</strong></td>
                      <td>£{formatPrice(props.product.pricing.buyUsed?.purchasePrice)}</td>
                      <td>£{addVat(props.product.pricing.buyUsed?.purchasePrice)}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

          }


          {/* Footer  */}
          <div className="searchCard-layout-footer">

            {/* Small Footer */}
            <div className="searchCard-smallCta">
              <Link href="/products/[slug]/[[...tab]]" as={`/products/${props.product.slug}`}>
                <span className="searchCard-smallCta-text">PRODUCT INFORMATION</span>
                <span className="searchCard-smallCta-icon">
                  <Icon iconName="chevron" iconDirection="right" />
                </span>
              </Link>
            </div>

            {/* large Footer  */}
            <div className="searchCard-footer">

              <div className="searchCard-call">
                <span className="searchCard-call-icon">
                  <Icon iconName="contact" />
                </span>
                <span className="searchCard-call-text">CALL US FOR ADVICE ON <strong>0800 025 8005</strong></span>
              </div>

              <Link href="/products/[slug]/[[...tab]]" as={`/products/${props.product.slug}`} className="searchCard-cta">
                <span className="searchCard-cta-text">FULL INFORMATION AND PRICING</span>
                <span className="searchCard-cta-icon">
                  <Icon iconName="chevron" iconDirection="right" />
                </span>
              </Link>

            </div>

          </div>

        </div>

      </StyledComponent>

    </>
  
  );

}


export default Component;




// Styles
const StyledComponent = styled.div`

  
  border-top: solid 1px ${color.lines};
  border-bottom: solid 1px ${color.lines};
  background-color: white;

  .searchCard-layout{
    display: grid;
    width: 100%;
    grid-template-columns: 30% 70%;
    grid-template-areas: "images details" "pricing pricing" "footer footer";
  }

  .searchCard-layout-images {
    grid-area: images; 
    padding: 16px;
  }

  .searchCard-layout-details { 
    grid-area: details;
    padding: 16px;
    padding-right: 24px;
  }

  .searchCard-layout-pricing { 
    display: flex;
    align-items: flex-end;
    grid-area: pricing;
  }

  .searchCard-layout-footer { 
    grid-area: footer;
  }



  /* Images  */
  .searchCard-mainImage{

    display: flex;
    align-items: center;
    justify-content: center;

    a{
      display: block;
      width: 100%;
      text-align: center;
    }

    img{
      width: 100%;
    }

  }

  .searchCard-thumbs{

    display: none;
    justify-content: space-around;
    width: 100%;
    margin-top: 10px;

    a{
      display: block;
      width: 25%;
    }

    img{
      width: 100%;
    }

  }

  .searchCard-southEast{

    display: none;
    align-items: center;
    margin-top: 20px;

    .searchCard-southEast-pic{

      flex: 0 0 auto;
      position: relative;
      top: 4px;
      width: 50px;

      img{width: 100%;}

    }

    .searchCard-southEast-text{
      flex: 1 1 auto;
      font-family: ${theme.typography.fontFamilySecondary};
      font-size: 12px;
      margin-left: 4px;
    }

  }


  /* Deails */
  .searchCard-title{
    font-size: 18px;
    font-family: ${theme.typography.fontFamilySecondary};
    color: ${color.primary};
    line-height: 1.4;
  }

  .searchCard-summary{
    margin-top: 0.5rem;
    font-size: 14px;
  }


  /* Pricing */
  .searchCard-pricingTable{

    width: 100%;
    font-family: ${theme.typography.fontFamilySecondary};
    font-size: 13px;

    tr{
      th:nth-child(even), td:nth-child(even){
        background-color: ${color.grey(92)};
      }
    }

    th{
      border-bottom: solid 1px rgba(229, 232, 235, 1);
    } 
    
    tr:not(:last-child) td{
      border-bottom: solid 1px rgba(229, 232, 235, 0.5);
    }

    th, td{
      padding: 5px 10px;
    }

    td:not(:first-child){
      text-align: center;
    }

  }



  /* Footer */
  .searchCard-smallCta{
    
    a{

      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 36px;
      color: white;
      font-family: ${theme.typography.fontFamilySecondary};
      font-size: 12px;
      font-weight: bold;
      line-height: 1;
      background-color: ${color.primary};
      transition: opacity 0.15s ease-out;

      .icon{

        width: 10px;
        height: 10px;
        margin-left: 6px;

        svg{
          fill: white;
        }

      }

      &:hover, &:focus{
        opacity: 0.75;
      }

    }

  }


  .searchCard-footer{
    display: none;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    font-family: ${theme.typography.fontFamilySecondary};
    font-size: 16px;
    border-top: solid 1px ${color.lines};
  }

  .searchCard-call{

    display: flex;
    align-items: center;
    height: 100%;
    padding-left: 20px;

    .searchCard-call-icon{

      margin-right: 8px;

      .icon{
        width: 28px;
        height: 28px;
      }

      svg{
        fill: ${color.grey(30)};
      }

    }

    .searchCard-call-text{
      color: ${color.grey(30)};
    }

  }

  .searchCard-cta{

    display: flex;
    align-items: center;
    padding-left: 30px;
    padding-right: 30px;
    color: ${color.primary};
    font-weight: bold;
    height: 100%;
    transition: opacity 0.15s ease-out;

    .icon{

      margin-left: 8px;
      width: 14px;
      height: 14px;

      svg{
        fill: ${color.primary};
      }

    }

    &:hover, &:focus{
      opacity: 0.75;
    }

  }


  @media (min-width: ${bp(2)}) {

    .searchCard-layout{
      grid-template-columns: 20% 80%;
    }

  }



  @media (min-width: ${bp(3)}) {

    border-left: solid 1px ${color.lines};
    border-right: solid 1px ${color.lines};

    .searchCard-layout{
      grid-template-columns: 240px 1fr;
      grid-template-areas: "images details" "images pricing" "footer footer";
    }
    

    .searchCard-layout-details { 
      padding: 24px;            
    }

    .searchCard-layout-images {
      border-right: solid 1px ${color.lines};
    }

    .searchCard-mainImage{

      img{
        width: 80%;
      }

    }

    .searchCard-thumbs{
      display: flex;
    }

    .searchCard-southEast{
      display: flex;
    }

    .searchCard-title{
      font-size: 26px;
    }


    .searchCard-smallCta{
      display: none;
    }

    .searchCard-footer{
      display: flex;
    }

  }


`;



